import {
  Checkbox,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import IconWithTooltip from "components/IconWithTooltip";
import DocumentsTable from "components/Modal/CompareDocumentsModal/DocumentsTable";
import Switch from "components/Switch";
import { checkedTypes, modalTypes } from "constants/types";
import { ReactComponent as InfoIcon } from "icons/info-grey.svg";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal } from "slices/app/actions/modals";
import { requestPoll } from "slices/app/actions/poller";
import { removeErrorMessage } from "slices/app/errorSlice";
import {
  compareDocuments,
  statusCompareDocuments,
} from "slices/workspace/actions/workspace";
import { getDocumentNode } from "slices/workspace/navigationNodes";

const getModalTitle = (type) =>
  type === "not-excel" ? "Compare Documents" : "Compare Excel Workbooks";

const getBodyText = (type) =>
  type === "not-excel" ? (
    <span>
      Check the 2 documents you wish to compare, and click the Compare button
      below.
    </span>
  ) : (
    <span>
      Check the 2 workbooks you wish to compare, and click the Compare button
      below.
      <br />
      If the Excel download fails to open or appears empty,{" "}
      <a
        target="_blank"
        href="https://support.visiblethread.com/hc/en-us/articles/214226226-Why-do-I-get-an-empty-Excel-file-when-I-try-to-compare-two-documents"
        rel="noreferrer"
      >
        please see this guide for workarounds
      </a>
    </span>
  );

const tooltipText =
  "<span class='compare_documents_modal_tooltip'>Enter words or phrases that you wish " +
  "to exclude from the comparison e.g. page numbers, appendix references.<br/>Use multiple lines to exculde " +
  "multiple items<br/><br/>Check &quot;Regular Expression&quot; to treat each line as a regular expression.<br/><br/>" +
  "Use this to ignore certain text in your output. If the sentence contains the specified text, then the full sentence " +
  "will be ignored.</span>";

const CompareDocumentsModal = ({ type, folderId, docId }) => {
  const dispatch = useDispatch();
  const excel = type === "excel";

  const workspace = useSelector((state) => state.workspace);
  const checkedDocByDefault = docId
    ? getDocumentNode(workspace.navigationInfo, Number(docId))
    : null;
  const [allDocsInWorkspace, setAllDocsInWorkspace] = useState(false);
  const [baselineDoc, setBaselineDoc] = useState();
  const [latestDoc, setLatestDoc] = useState();
  const [regularExpression, setRegularExpression] = useState(false);
  const [phrases, setPhrases] = useState("");

  const handleSuccess = (d) => {
    window.location = d?.data?.jobMetaInfo?.file;
    handleCancel();
  };

  const handleFailure = (error) => {
    handleCancel();
    dispatch(showErrorModal(error));
  };

  const handleOk = () => {
    if (!latestDoc || !baselineDoc) {
      return;
    }
    dispatch(removeErrorMessage);
    dispatch(
      requestPoll(
        compareDocuments({
          excludeText: phrases,
          newDocumentId: latestDoc.vtid,
          oldDocumentId: baselineDoc.vtid,
          reportType: excel ? "excelCompareExcel" : "excelCompareDocs",
          useRegex: regularExpression,
        }),
        statusCompareDocuments,
        handleSuccess,
        handleFailure
      )
    );
  };

  const handleCancel = () => {
    dispatch(removeErrorMessage);
    dispatch(hideModal(modalTypes.COMPARE_DOCUMENTS));
  };

  const handleSwitchChange = () => {
    setAllDocsInWorkspace(!allDocsInWorkspace);
  };

  const handleDocsChange = (baseline, latest) => {
    setBaselineDoc(baseline);
    setLatestDoc(latest);
  };

  return (
    <Modal
      data-testid="compare-modal"
      className="fade-in compare_documents_modal"
      show
      onDismiss={handleCancel}
      centered
    >
      <Modal.Header>
        <b>{getModalTitle(type)}</b>
      </Modal.Header>
      <Modal.Body style={{ whiteSpace: "break-spaces" }}>
        {getBodyText(type)}

        <div className="view_switch">
          <span className="">All docs in workspace</span>
          <Switch
            currentSwitchState={allDocsInWorkspace}
            handleChange={handleSwitchChange}
            id="compare-docs-modal-switch"
          />
        </div>

        <div className="docs_table_wrapper">
          <DocumentsTable
            allDocsInWorkspace={allDocsInWorkspace}
            folderId={Number(folderId)}
            excel={excel}
            checkedDocByDefault={checkedDocByDefault}
            onChange={handleDocsChange}
          />
        </div>

        <span className="selections">
          <span className="selection">
            <span className="bolder">Baseline:</span>
            <span
              dangerouslySetInnerHTML={{
                __html: baselineDoc ? baselineDoc.text : "",
              }}
            />
          </span>
          <span className="selection">
            <span className="bolder">Latest:</span>
            <span
              dangerouslySetInnerHTML={{
                __html: latestDoc ? latestDoc.text : "",
              }}
            />
          </span>
        </span>
        <br />

        <span className="separator"></span>

        <span className="advanced-title">Advanced Options</span>
        <div className="advanced-options">
          <div className="column1">
            <span className="bolder">
              Exclude Text
              <IconWithTooltip
                icon={<InfoIcon width={11} height={11} />}
                tooltipText={tooltipText}
              />
              <br />
            </span>

            <span className="regular-expression">
              <Checkbox
                state={
                  regularExpression ? checkedTypes.CHECKED : checkedTypes.EMPTY
                }
                handleChange={() => {
                  setRegularExpression(!regularExpression);
                }}
              />
              Regular Expresison
            </span>
          </div>
          <div className="column2">
            <textarea
              onChange={(e) => {
                setPhrases(e.target.value);
              }}
              placeholder="Enter words/phrases to exclude from comparison e.g.&#10;Appendix A&#10;Appendix B"
            ></textarea>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancel-button"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-testid="compare-button"
          variant={NewButtonVariant.l1}
          onClick={handleOk}
          disabled={!baselineDoc || !latestDoc}
        >
          Compare
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default CompareDocumentsModal;

CompareDocumentsModal.propTypes = {
  type: PropTypes.string.isRequired,
  folderId: PropTypes.string.isRequired,
  docId: PropTypes.string,
};
